import { Pagination } from '@toctoc/design-system';
import { IPaginationIndex } from './Pagination.interfaces';
import { useAppDispatch, useAppSelector } from 'src/app/hooks'
import { setPage, selectFilter } from 'src/app/reducers/filters'
import { showPage, selectAllResultsCount, selectBatchStartPage, fetchByCriteria } from 'src/app/reducers/properties'
import { Grid } from '@mui/material'
import { ChangeEvent, useEffect, useRef } from 'react';
import { getDataAttributes } from '@/utils/object';
import { useRouter } from 'next/router';

const DEFAULT_BATCH_SIZE = 250; // debe reflejar el default del backend cuando no viaja `limit`

export const PaginationIndex = (dataPagination: IPaginationIndex) => {

	const { totalPropiedades, batchEnabled = true, limit, ...props } = dataPagination;
	const clientPagesPerBatch = Math.ceil((limit ?? DEFAULT_BATCH_SIZE) / 20);
	const dispatch = useAppDispatch();
	const allResultsCount = useAppSelector(selectAllResultsCount);
	const batchStartPage = useAppSelector(selectBatchStartPage);
	const filterState = useAppSelector(selectFilter);
	const router = useRouter();
	const dataAttr = getDataAttributes(props)
	const pageFromUrl = Number(router.query.pagina) || 1;
	// Evita que el useEffect procese páginas que ya manejamos en handlePagination
	const lastPushedPage = useRef<number | null>(null);
	// En modo V1 (batchEnabled=false) no usamos filterState.page como guard porque setPage
	// cambia filterState y dispara usePropertySearch innecesariamente. Usamos este ref en su lugar.
	const lastAppliedPage = useRef<number>(1);

	const batchEndPage = batchStartPage + Math.ceil(allResultsCount / 20) - 1;
	const isClientSidePage = (page: number) =>
		allResultsCount > 20 && page >= batchStartPage && page <= batchEndPage;

	const applyPage = async (page: number) => {
		if (!batchEnabled) {
			// V1: fetch the exact page from the API without touching filterState.page,
			// to avoid triggering usePropertySearch via filterState reference change.
			lastAppliedPage.current = page;
			await dispatch(fetchByCriteria({ ...filterState, page, limit }));
		} else {
			dispatch(setPage(page));
			if (isClientSidePage(page)) {
				dispatch(showPage(page));
			} else {
				const batchNumber = Math.ceil(page / clientPagesPerBatch);
				await dispatch(fetchByCriteria({ ...filterState, page: batchNumber, limit }));
				dispatch(showPage(page));
			}
		}
	};

	const handlePagination = async (_event: ChangeEvent<unknown>, value: number) => {
		lastPushedPage.current = value;
		// Build URL from router.asPath string to preserve exact encoding (e.g. unencoded commas
		// in precios values). Using { query } object would re-encode commas as %2C, causing a
		// mismatch with constructFlexibleUrl output and breaking the stripPagina guard in Filters.tsx
		const [currentPath, currentSearch] = router.asPath.split('?');
		const searchParts = (currentSearch || '')
			.split('&')
			.filter(part => part && !part.startsWith('pagina='));
		if (value !== 1) {
			searchParts.push(`pagina=${value}`);
		}
		const newUrl = currentPath + (searchParts.length > 0 ? '?' + searchParts.join('&') : '');
		router.push(newUrl, undefined, { shallow: true });
		applyPage(value);
	};

	// Maneja navegación back/forward del navegador
	useEffect(() => {
		// Si fue un push nuestro, ignorar (ya aplicamos la página en handlePagination)
		if (lastPushedPage.current === pageFromUrl) {
			lastPushedPage.current = null;
			return;
		}
		// Guard: evitar re-aplicar la misma página
		const currentPage = batchEnabled ? filterState.page : lastAppliedPage.current;
		if (pageFromUrl === currentPage) return;
		applyPage(pageFromUrl);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.query.pagina]);

	const pageCount = Math.ceil(totalPropiedades / 20);

	return (
		<div>
			<Grid container justifyContent={{ md: 'flex-end'} }sx={{padding : '24px 0px'}} >
				<Pagination
					key={String(pageFromUrl)}
					count={pageCount}
					defaultPage={pageFromUrl}
					handlePagination={handlePagination}
					{...dataAttr}
				/>
			</Grid>
		</div>
	)
}
